import {
  ApiError,
  type CatalogueItemResource,
  type OutletResource,
  type SaleLineResource,
  type SaleReprintResource,
  SaleReprintsController_reprint_v1,
  type SaleResource,
  SalesController_read_v1,
  type SaleTaxResource,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { discountReasonLabel } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { IconAlertTriangle, IconArrowLeft, IconReceiptOff } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound, messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { describeRecordingGap, formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { LoadFailed, Screen, ScreenFact } from '../back-office/Screen';
import { paths } from '../paths';
import { labelForPaymentMode, salePayments } from './payment-modes';
import { ReceiptLookup } from './ReceiptLookup';
import { RefundSaleDialog } from './RefundSaleDialog';
import { ReprintReceiptDialog } from './ReprintReceiptDialog';
import { useBasketItems, useNamedOutlet } from './sale-names';
import { isRefundable } from './sale-refund';
import { LineTaxCells, LineTaxHeads, SaleTaxSection } from './SaleTax';
import { formatTradingDay } from './trading-day';

/**
 * One sale's record: what a customer disputing a charge is shown.
 *
 * ## Why a page and not a row
 *
 * The list answers "what did the outlets take today". This answers "what is
 * *this*", and the two questions want different things on screen. A row is
 * scanned in a column of its neighbours and carries what discriminates between
 * them — which is why the list shows a basket as `lines.length`. A record is read
 * on its own with somebody standing at the counter, and what it needs is the
 * whole answer: **the basket line by line**, what was paid and how, who sold it,
 * both clocks, and the refund if there is one.
 *
 * It also gives the lookup somewhere to land that can be refreshed, kept open in
 * a second tab, and read out to a colleague. That is the whole reason the address
 * is the sale's **id** and not its receipt number: the number is
 * `(terminal, counter, value)` and two tills can print the same one — see
 * `ReceiptLookup.tsx` — while the id names one row for good.
 *
 * ## Nothing here edits the sale, and nothing ever will
 *
 * A recorded sale is never mutated. A refund is a **compensating entry** — a
 * separate row that reverses it (R-408) — and the sale stays exactly as it was
 * rung up, which is why this screen has no edit affordance for the sale itself
 * and no control that could grow into one.
 *
 * The screen does now carry two **acts**, and neither is an edit:
 *
 * - **Refund** writes a new record and a reversing entry in the books, and
 *   leaves the sale exactly as it was. The sale is then shown marked — the same
 *   marks it would carry if somebody else had refunded it an hour ago.
 * - **Reprint** writes nothing at all except an audit row: it consumes no
 *   receipt number, advances no counter and changes no field, and what comes
 *   back is the sale marked a duplicate.
 *
 * Their reasoning lives in `RefundSaleDialog.tsx` and `ReprintReceiptDialog.tsx`
 * respectively, including which refusals each can meet and why neither words one
 * itself.
 *
 * **Neither control checks a role, and neither should.** `SaleRefundsController`
 * and `SaleReprintsController` both declare `@RequireRole()` with no arguments
 * and `@RequireOutletScope()`: the rule is *reach* rather than rank — anyone who
 * may sell at an outlet may refund and reprint there (CL-403, FR-414b). This
 * route is narrower than that (`OWNER`, `MANAGER`), which is the route's own
 * decision and is argued in `route-table.tsx`; a second, invented rank inside the
 * screen would hide a control the API grants.
 *
 * ## Names are a courtesy; the record is the record
 *
 * The outlet, the basket's catalogue items and the cashier are read separately,
 * and **a failure to read any of them costs a name and never the screen**. The
 * sale itself already carries every fact that decides a dispute; what those reads
 * buy is "Tea, 2" instead of two UUIDs.
 *
 * `Terminals` is not read at all: the directory is `OWNER`-only on the API, and
 * the receipt number already carries the till's own prefix (FR-410), which is
 * narrower than the outlet and is the part printed on the paper. A separate
 * Terminal fact would be a UUID nobody can act on.
 */

/** One read, in one of the three states it can be in. */
type Loaded<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly value: T }
  | { readonly kind: 'failed'; readonly error: unknown };

export function Sale() {
  const client = useApiClient();
  const { id } = useParams<{ id: string }>();

  const [state, setState] = useState<Loaded<SaleResource>>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  /** Whether the refund confirmation is open. The dialog holds the refund's ids. */
  const [refunding, setRefunding] = useState(false);

  /** The duplicate on screen, and whether one is being asked for. */
  const [reprint, setReprint] = useState<SaleReprintResource | null>(null);
  const [reprinting, setReprinting] = useState(false);
  const [reprintError, setReprintError] = useState<string | null>(null);

  useEffect(() => {
    if (id === undefined) return;

    // Two guards, not one. `controller` stops the request itself, and `live`
    // stops an answer that was already in flight from being written into state
    // after this effect was torn down — which is what a second lookup done from
    // this very screen produces.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(SalesController_read_v1, { path: { id }, signal: controller.signal })
      .then((sale) => {
        if (live) setState({ kind: 'loaded', value: sale });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, id, reloads]);

  const sale = state.kind === 'loaded' ? state.value : null;

  const outlet = useNamedOutlet(sale?.outletId);
  const items = useBasketItems(sale?.lines);
  const cashier = useCashierName(sale?.actorId);

  // A route matched on `:id` always has one; this is the shape of somebody
  // reaching the URL some other way. A redirect rather than a not-found screen,
  // because the sales list is where they were going.
  if (id === undefined) return <Navigate to={paths.sales} replace />;

  // **`RESOURCE_NOT_FOUND` and `VALIDATION_FAILED` are one answer here, and that
  // is not a conflation.** The first is a sale that does not exist *or* is
  // outside this caller's reach — indistinguishable on purpose (FR-437, FR-128) —
  // and the second is an address that is not a UUID at all, which the API refuses
  // before it looks anything up. All three reach this screen the same way: a link
  // that is out of date, or a URL typed by hand. There is nothing here that can
  // tell them apart and nothing below that pretends to.
  const missing =
    state.kind === 'failed' &&
    (isNotFound(state.error) ||
      (state.error instanceof ApiError && state.error.code === 'VALIDATION_FAILED'));

  const refundedAt = sale?.refundedAt ?? null;

  /**
   * Ask for a duplicate.
   *
   * **A fresh idempotency key per press, which is the right reading of the rule
   * for a screen that stays open.** Two presses are two genuine reprints and
   * leave two audit rows — the API says so in as many words — so a key held for
   * the component's life would present one `(scope, key)` twice and replay the
   * first answer instead. What the key buys is that *one* press whose answer was
   * lost is one act rather than two if it is ever re-sent.
   *
   * Nothing about the sale changes, so nothing is re-read afterwards: the answer
   * is the sale as the read paths publish it, marked, and a second round trip
   * would only be a chance for it to arrive stale.
   */
  const reprintReceipt = async (saleId: string) => {
    if (reprinting) return;

    setReprinting(true);
    setReprintError(null);

    try {
      setReprint(
        await client.request(SaleReprintsController_reprint_v1, {
          path: { id: saleId },
          headers: { [IDEMPOTENCY_KEY_HEADER]: newIdempotencyKey() },
        }),
      );
    } catch (error: unknown) {
      // Worded from the code like every other refusal. What this route publishes
      // for a *session* caller is `VALIDATION_FAILED`, `RESOURCE_NOT_FOUND` and
      // `AUTH_CREDENTIAL_CHANGE_REQUIRED`, none of which is anything a person
      // did here — a sale that was on screen a moment ago and cannot be
      // reprinted has been reached with a stale link or by somebody whose reach
      // changed under them.
      //
      // `blossom-rest-api#411` added `401` and three `403` codes to the
      // document for this operation, and none of them is reachable from this
      // screen: `AUTH_TERMINAL_REVOKED` and `AUTH_NO_OUTLET_ASSIGNMENT` are the
      // terminal half of the route, and a browser holds no lease to present.
      // `messageForFailure` words them anyway rather than this screen filtering
      // — a client that decided which refusals the server is allowed to send is
      // a client that renders a blank on the day it is wrong.
      setReprintError(messageForFailure(error));
    } finally {
      setReprinting(false);
    }
  };

  return (
    <Screen
      title="Sale"
      aside={
        <>
          {sale !== null && (
            <>
              <ScreenFact label="Receipt">
                <span className="font-mono numeric">{sale.receiptNumber}</span>
                {refundedAt !== null && (
                  <>
                    {' '}
                    {/* `outline` and not `destructive`, matching the list. Red in
                        this system means the system refused something or is about
                        to; a refund is an ordinary correction that already
                        happened. */}
                    <Badge variant="outline">Refunded</Badge>
                  </>
                )}
              </ScreenFact>

              <ScreenFact label="Total">
                {/* Struck through when refunded and still legible: the amount that
                    was taken and given back is exactly the figure somebody
                    reconciling the day needs to read. `formatPaise` is the only
                    thing that turns paise into an amount — nothing here divides by
                    100. */}
                <span className={`font-mono numeric ${refundedAt !== null ? 'line-through' : ''}`}>
                  {formatPaise(sale.totalPaise)}
                </span>
              </ScreenFact>
            </>
          )}

          <Link
            to={paths.sales}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the sales
          </Link>
        </>
      }
    >
      {/* Above the record, and on this screen as well as the list, because the
          next customer with a question arrives while somebody is still looking at
          the last one's receipt. */}
      <ReceiptLookup />

      {missing ? (
        <SaleMissing />
      ) : (
        // **No card.** The panel is the surface, and the record is the only
        // thing on the field under the lookup band — so its own edges were a
        // card inside a card twice over. The lookup's rule above it is the
        // boundary a box was drawing, and the records band supplies the gutter
        // the `p-6` was.
        <div className="pt-2">
          {state.kind === 'loading' && <RecordSkeleton />}

          {state.kind === 'failed' && !missing && (
            <div className="mx-auto max-w-md space-y-3 py-8 text-center">
              <LoadFailed error={state.error} onRetry={reload} />
            </div>
          )}

          {sale !== null && (
            <Record
              sale={sale}
              outlet={outlet}
              items={items}
              cashier={cashier}
              reprinting={reprinting}
              reprintError={reprintError}
              onRefund={() => {
                setRefunding(true);
              }}
              onReprint={() => {
                void reprintReceipt(sale.id);
              }}
            />
          )}
        </div>
      )}

      <RefundSaleDialog
        sale={refunding ? sale : null}
        onClose={() => {
          setRefunding(false);
        }}
        onRefunded={(refund) => {
          setRefunding(false);
          // Written straight into state rather than re-read. The refund carries
          // the sale it reverses, as the read paths publish it and already
          // marked, for both `201` and the `200` that answers the same refund
          // re-sent — so this is the authoritative copy and a second round trip
          // would only be a chance for it to arrive stale.
          setState({ kind: 'loaded', value: refund.sale });
        }}
      />

      <ReprintReceiptDialog
        reprint={reprint}
        items={items}
        onClose={() => {
          setReprint(null);
        }}
      />
    </Screen>
  );
}

/**
 * The record itself.
 *
 * Every mark a refunded sale carries is the one the list row carries — the struck
 * total, the `outline` badge, the muted treatment — because somebody who saw the
 * row this morning must recognise the same sale this afternoon.
 *
 * **The two controls at the foot of it change nothing on this record.** A refund
 * writes a separate row and a reversing entry; a reprint writes an audit row and
 * hands back a duplicate. Neither is an edit, and the sale itself is never
 * editable by anybody, because it is never mutated.
 */
function Record({
  sale,
  outlet,
  items,
  cashier,
  reprinting,
  reprintError,
  onRefund,
  onReprint,
}: {
  sale: SaleResource;
  outlet: OutletResource | null;
  items: ReadonlyMap<string, CatalogueItemResource>;
  cashier: string | null;
  reprinting: boolean;
  reprintError: string | null;
  onRefund: () => void;
  onReprint: () => void;
}) {
  const refundedAt = sale.refundedAt;
  const refunded = refundedAt !== null;

  const gap = describeRecordingGap(sale.occurredAt, sale.recordedAt);

  const struck = refunded ? 'text-muted-foreground line-through' : 'text-foreground';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="space-y-2">
          <p
            className={`font-mono text-2xl numeric ${refunded ? 'text-muted-foreground' : 'text-foreground'}`}
          >
            {sale.receiptNumber}
          </p>

          {refunded && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">Refunded</Badge>
            </div>
          )}
        </div>

        <div className="text-right">
          <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
            Paid
          </p>
          <p className={`mt-1 font-mono text-2xl numeric ${struck}`}>
            {formatPaise(sale.totalPaise)}
          </p>
        </div>
      </div>

      {refunded && (
        <Refunded refundedAt={refundedAt} amountPaise={sale.totalPaise} tax={sale.tax} />
      )}

      <Basket
        lines={sale.lines}
        items={items}
        discountPaise={sale.discountPaise}
        discountReason={sale.discountReason}
        totalPaise={sale.totalPaise}
        refunded={refunded}
        taxed={sale.tax.status === 'TAXED'}
      />

      <SaleTaxSection tax={sale.tax} />

      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border pt-6 sm:grid-cols-2">
        <Fact label="Outlet">
          {outlet === null ? (
            // Not a blank and not a guess. The outlet read may have failed or may
            // still be in flight, and the id is what somebody quotes when they ask
            // about it.
            <span className="font-mono text-xs numeric">{sale.outletId}</span>
          ) : (
            outlet.name
          )}
        </Fact>

        <Fact label="Paid by">
          {/* One part for a sale paid one way, read exactly as it always was;
              each part with its amount on a split payment (#357). Through
              `salePayments`, so a server that sends no parts still says how. */}
          {salePayments(sale).map((part, _index, parts) => (
            <span key={part.paymentMode} className="block">
              {labelForPaymentMode(part.paymentMode)}
              {parts.length > 1 && (
                <span className="ml-2 font-mono numeric">{formatPaise(part.amountPaise)}</span>
              )}
              {/* Only where one was submitted, which is every card or UPI sale
                  taken with an approval code and no cash sale at all (FR-408b).
                  Opaque and never parsed — it is quoted back to a bank or a
                  payment app, so it gets the mono face like every other value
                  read aloud. */}
              {part.paymentReference !== null && (
                <span className="mt-0.5 block font-mono text-sm text-muted-foreground numeric">
                  {part.paymentReference}
                </span>
              )}
            </span>
          ))}
        </Fact>

        <Fact label="Rung up">{formatInstant(sale.occurredAt)}</Fact>

        <Fact label="Recorded">
          {formatInstant(sale.recordedAt)}
          {/* The gap is the account of an outage and is never worded as a fault:
              a sale that took four hours to arrive is a sale the offline queue
              did its job for. */}
          {gap !== null && (
            <span className="mt-0.5 block text-sm text-muted-foreground">{gap}</span>
          )}
        </Fact>

        <Fact label="Trading day">
          {/* Derived from `recordedAt`, never the raw `businessDayId` — the id is
              internal and a UUID in front of somebody reconciling a till is noise
              they cannot act on. The caveat on the derivation is in
              `trading-day.ts`. */}
          {formatTradingDay(sale.recordedAt)}
        </Fact>

        <Fact label="Sold by">
          {cashier ?? <span className="font-mono text-xs numeric">{sale.actorId}</span>}
          {/* A bare reference when the name is out of reach. The staff
              directory is the owner's — `UsersController` carries
              `@RequireRole('OWNER')` on every operation and CL-104 is explicit
              that a manager does not manage people — so a manager reading a sale
              they are entitled to read cannot put a name to the person who took
              it. Reported in the PR; it is an API gap, not something to infer. */}
        </Fact>

        <Fact label="Reference">
          <span className="font-mono text-xs numeric">{sale.id}</span>
        </Fact>
      </dl>

      {/* Below the record and not beside the number, for the reason the ticket
          record gives: these are read before they are pressed, and a control at
          the top is one somebody reaches before they have finished reading which
          sale they are looking at.

          Both `outline`, including the refund. The red is spent inside the
          confirmation, where the irreversible thing actually happens — a screen
          whose strongest colour sits permanently on a record has nowhere left to
          go when it needs to warn. */}
      <div className="space-y-3 border-t border-border pt-6">
        {reprintError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{reprintError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <Button
            variant="outline"
            // The number rather than "Reprint": what the customer is asking
            // about is the receipt, and the number is what they read out.
            aria-label={`Reprint ${sale.receiptNumber}`}
            disabled={reprinting}
            onClick={onReprint}
          >
            {reprinting ? 'Reprinting…' : 'Reprint receipt'}
          </Button>

          {/* Offered only where there is something left to reverse. A refunded
              sale is still reprintable — the receipt is the record of what
              happened, and a customer disputing a refunded charge needs it —
              which is why only this one is conditional. */}
          {isRefundable(sale) && (
            <Button
              variant="outline"
              aria-label={`Refund ${sale.receiptNumber}`}
              onClick={onRefund}
            >
              Refund this sale
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The refund, said in a sentence as well as in the marks above it.
 *
 * This is the answer to the question somebody came to the counter with, so it is
 * stated rather than left to a struck-through figure. Three things are worth
 * saying and each is here: **that the money went back**, **when the system
 * accepted the reversal**, and **that the books carry a compensating entry rather
 * than an edit** — the sale is exactly as it was rung up, and a day whose takings
 * are short is explained by rows like this one.
 *
 * **The moment is the server's clock**, and the wording says so. `refundedAt` is
 * projected from `sale_refunds.recorded_at` (`sale-sql.ts`), not from the
 * `occurredAt` the refunding caller submitted — the API records that one and
 * refuses to let it order or decide anything (FR-008). Calling this "when it was
 * refunded" without qualification would be attributing a device's account to the
 * system's.
 *
 * **Who refunded it and why are not shown, and cannot be.** `SaleRefundResource`
 * carries `actorId`, `reason` and both clocks, and it is returned by exactly one
 * operation in the shipped document — `POST /v1/sales/{id}/refund` — so the only
 * caller who ever sees them is the one performing the refund. `SaleResource`
 * carries `refundedAt` and nothing else about it, and there is no `GET` for a
 * refund anywhere. So a record opened afterwards cannot say who reversed it. The
 * ticket asks for "by whom"; it is a gap in the API rather than a decision taken
 * here, reported in the PR, and it is not something to guess at from the sale's
 * own `actorId` — the person who sold it is very often not the person who
 * refunded it, and rendering one as the other would be a plausible, wrong name on
 * a dispute record.
 */
function Refunded({
  refundedAt,
  amountPaise,
  tax,
}: {
  refundedAt: string;
  amountPaise: number;
  tax: SaleTaxResource;
}) {
  return (
    <div className="rounded-3xl border border-border bg-muted/40 p-4">
      <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        Refunded
      </p>
      <p className="mt-2 text-sm text-foreground">
        <span className="font-mono numeric">{formatPaise(amountPaise)}</span> went back to the
        customer on {formatInstant(refundedAt)}.
      </p>
      {/* The refund carries the sale's tax figures negated — every one of them
          (blossom-rest-api#524) — so the invoice is reversed whole, and naming
          it is all there is to add. */}
      {tax.status === 'TAXED' && (
        <p className="mt-2 text-sm text-foreground">
          Tax invoice <span className="font-mono numeric">{tax.invoiceNumber}</span> is reversed
          with it.
        </p>
      )}
    </div>
  );
}

/**
 * The basket, line by line — the thing this screen exists for and the one thing
 * the list deliberately does not show.
 *
 * **No unit price, and its absence is considered.** `SaleLineResource` carries
 * `quantity` and the line's `amountPaise` and no per-item figure; the price row it
 * was rung up against is named by `cataloguePriceId`, which only
 * `CataloguePricesController` can resolve and which is `OWNER`-only because every
 * row of it carries `costPaise`. Dividing the line by its quantity would be this
 * client computing money — the one thing `lib/money.ts` exists to stop — to
 * produce a figure the API never published. What a customer is disputing is the
 * line's amount, and that is a fact the server sent.
 *
 * **On a tax invoice each line carries its HSN, rate, taxable value and tax**,
 * as the API stored them, and the columns appear only then. On an untaxed or
 * unrecorded sale they would be a column of blanks — or, worse, of zeros that
 * claim a tax nobody knows.
 *
 * **The total is the server's `totalPaise`, not a sum of these rows.** The API
 * redoes that addition and refuses a basket whose lines disagree with it (FR-419),
 * so the client adding them up again could only ever produce a second opinion —
 * and `docs/ui-conventions.md` is explicit that totals come from the API.
 */
function Basket({
  lines,
  items,
  discountPaise,
  discountReason,
  totalPaise,
  refunded,
  taxed,
}: {
  lines: readonly SaleLineResource[];
  items: ReadonlyMap<string, CatalogueItemResource>;
  /**
   * The whole-bill discount (#553), on its own line under the items. The lines
   * keep what they listed at, and the tax figures already came net of it — so
   * nothing here takes it off anything.
   */
  discountPaise: number;
  discountReason: string | null;
  totalPaise: number;
  refunded: boolean;
  taxed: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-border">
      <Table>
        <caption className="sr-only">What was bought</caption>

        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            {/* Counts right-align with the amounts beside them: both are read by
                comparing down the column. */}
            <TableHead className="w-24 text-right">Quantity</TableHead>
            {taxed && <LineTaxHeads />}
            <TableHead className="w-36 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {lines.map((line) => (
            <BasketLine
              key={line.id}
              line={line}
              item={items.get(line.catalogueItemId) ?? null}
              taxed={taxed}
            />
          ))}

          {discountPaise > 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell className="text-foreground">
                Discount
                {discountReason !== null && (
                  <span className="ml-2 text-muted-foreground">
                    {discountReasonLabel(discountReason)}
                  </span>
                )}
              </TableCell>
              <TableCell colSpan={taxed ? 5 : 1} />
              <TableCell className="text-right font-mono text-foreground numeric">
                −{formatPaise(discountPaise)}
              </TableCell>
            </TableRow>
          )}

          <TableRow className="hover:bg-transparent">
            <TableCell className="font-medium text-foreground">Total</TableCell>
            <TableCell colSpan={taxed ? 5 : 1} />
            <TableCell className="text-right font-mono numeric">
              <span className={refunded ? 'text-muted-foreground line-through' : 'text-foreground'}>
                {formatPaise(totalPaise)}
              </span>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * One line of the basket.
 *
 * **`itemRetiredAtSale` is a quiet badge and never a warning.** It means the item
 * was retired at the moment the *server* accepted the sale, evaluated once and
 * stored (FR-420, R-306). The sale is valid, nothing was refused and nobody did
 * anything wrong — the money is in the drawer and the customer walked out with the
 * goods. It is per line rather than per sale because a basket can mix a live item
 * with a retired one.
 */
function BasketLine({
  line,
  item,
  taxed,
}: {
  line: SaleLineResource;
  item: CatalogueItemResource | null;
  taxed: boolean;
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-normal">
        <div className="flex flex-wrap items-center gap-2">
          {item === null ? (
            // Not a blank and not a guess. The item read may have failed or may
            // still be in flight, and the id is what somebody quotes when they ask
            // about it. A retired item still answers that read, which matters: a
            // sale outlives the item it was sold against.
            <span className="font-mono text-xs numeric">{line.catalogueItemId}</span>
          ) : (
            <>
              <span className="text-foreground">{item.name}</span>
              <span className="font-mono text-sm text-muted-foreground numeric">{item.code}</span>
            </>
          )}

          {/* `secondary` rather than `outline`, so it reads as a note and not as
              the refund badge above it. */}
          {line.itemRetiredAtSale && <Badge variant="secondary">Retired item</Badge>}
        </div>
      </TableCell>

      <TableCell className="text-right font-mono text-foreground numeric">
        {line.quantity}
      </TableCell>

      {taxed && <LineTaxCells line={line} />}

      <TableCell className="text-right font-mono text-foreground numeric">
        {formatPaise(line.amountPaise)}
      </TableCell>
    </TableRow>
  );
}

/** One fact under its signage label, the way a standing fact is written. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-foreground">{children}</dd>
    </div>
  );
}

/**
 * The record's shape while it is in flight.
 *
 * A skeleton and not a spinner, matching what lands: `docs/ui-conventions.md` asks
 * for exactly this, because a layout shift under a pointer is how somebody clicks
 * the thing below the one they aimed at.
 *
 * `aria-hidden`, because there is nothing here to read.
 */
function RecordSkeleton() {
  return (
    <div aria-hidden className="space-y-6">
      <div className="flex items-start justify-between gap-8">
        <div className="h-8 w-44 animate-pulse rounded-3xl bg-muted" />
        <div className="h-8 w-28 animate-pulse rounded-3xl bg-muted" />
      </div>

      {/* The basket, which is the part somebody is waiting for. Three rows: a
          typical counter basket, so the page settles rather than jumps. */}
      <div className="space-y-3 rounded-3xl border border-border p-4">
        {[70, 55, 62].map((width) => (
          <div key={width} className="flex items-center justify-between gap-8">
            <div
              className="h-4 animate-pulse rounded-3xl bg-muted"
              style={{ width: `${String(width)}%` }}
            />
            <div className="h-4 w-20 shrink-0 animate-pulse rounded-3xl bg-muted" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border pt-6 sm:grid-cols-2">
        {/* Varied widths derived from the position rather than random, so it reads
            as text and does not re-roll on every render. */}
        {[62, 48, 70, 55, 40, 66].map((width) => (
          <div key={width} className="space-y-2">
            <div className="h-3 w-20 animate-pulse rounded-3xl bg-muted" />
            <div
              className="h-4 animate-pulse rounded-3xl bg-muted"
              style={{ width: `${String(width)}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * There is no sale at this address.
 *
 * **Byte for byte the same answer for a sale that does not exist and one outside
 * the caller's reach**, because the API answers them identically on purpose — a
 * distinguishable refusal is an enumeration oracle (FR-437, FR-128). This copy
 * says what is honest for both and nothing that implies it could tell.
 *
 * The way out is the lookup above it, which is already on the screen: somebody who
 * followed a stale link still has the receipt in their hand.
 */
function SaleMissing() {
  return (
    <Alert>
      <IconReceiptOff />
      <AlertTitle>There is no sale at this address</AlertTitle>
      <AlertDescription>Type the receipt&rsquo;s number into the field above.</AlertDescription>
    </Alert>
  );
}

/**
 * Who took the money, as a name — or `null` where there is no directory to ask.
 *
 * A sale names the **actor** the acting staff code resolved to, which is a
 * different identifier from the user's own (FR-439). `UserResource` carries both
 * and `actors.id` is what history is written against, so this maps on `actorId`;
 * keying it by `id` would produce a record with nobody's name on it and no error
 * anywhere.
 *
 * **There is no read-by-actor, which is why this reads the directory.**
 * `GET /v1/users/{id}` takes the *user's* id, and the sale does not carry one — so
 * the only way from an `actorId` to a name is the list. It is `OWNER`-only, so it
 * is not requested at all for anybody else: a request that can only 403 is not a
 * request worth making. The role comes out of `sessionStorage` and is editable, so
 * this is courtesy — somebody who edits it gets the refusal, which lands in the
 * `catch` below and leaves the record showing the reference instead.
 *
 * Read whole, which is `docs/ui-conventions.md`'s case 1: the staff of a park are
 * bounded by the park, and the sales list already reads them the same way.
 */
function useCashierName(actorId: string | undefined): string | null {
  const client = useApiClient();
  const { session } = useSession();
  const [name, setName] = useState<string | null>(null);

  // `UsersController` carries `@RequireRole('OWNER')` on every operation, and
  // CL-104 is explicit that the manager role does not manage people.
  const mayListPeople = session?.user.role === 'OWNER';

  useEffect(() => {
    if (actorId === undefined || !mayListPeople) {
      setName(null);

      return;
    }

    const controller = new AbortController();
    let live = true;

    collectPages<UserResource>((from, limit) =>
      client.request(UsersController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((people) => {
        if (!live) return;

        setName(people.find((person) => person.actorId === actorId)?.displayName ?? null);
      })
      .catch(() => {
        if (live) setName(null);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, actorId, mayListPeople]);

  return name;
}
